import { type ReactNode } from "react";
import { useLocation, useParams } from "react-router-dom";
import ChapterNav from "./ChapterNav";
import { FaceGateProvider } from "./FaceGateContext";
import ReadingPathway from "./ReadingPathway";
import { getActiveChartId } from "../../lib/chartSession";
import { JOURNEY_CHAPTERS } from "../../lib/journey";
import "./watch-shell.css";

/**
 * PageWithChapterNav — Watch-tab bezel shell.
 *
 * Task 4: derives the eligible pathway segments from JOURNEY_CHAPTERS
 * instead of a hardcoded PATHWAY_SEGMENTS set. The 8-chapter journey
 * config drives both the bezel and the pathway.
 *
 * - Bezel: shown when isWatchChapterPath(pathname) is true (delegated to
 *   primaryNav). Chapters 1–7 are disabled when no chart id is present.
 * - Pathway: shown when the current route belongs to one of the
 *   chart-bound chapters and a chart id is bound.
 *
 * S4 gutters/safe areas: the bezel row carries horizontal safe-area
 * insets (watch-shell.css) and the pathway keeps the standard
 * `page-inner` gutter so shell chrome and chapter content share one
 * left/right rhythm at every breakpoint.
 */

// Build the eligible-pathway set from the canonical config. Only the
// chart-bound chapters (id != 0) participate.
const PATHWAY_SEGMENTS = new Set(
  JOURNEY_CHAPTERS.filter((c) => c.needsChart).map((c) => c.baseRoute.replace(/^\//, "")),
);

function showPathway(pathname: string, chartId: string | null): boolean {
  if (!chartId) return false;
  const seg = pathname.split("/")[1] || "";
  return PATHWAY_SEGMENTS.has(seg);
}

export function PageWithChapterNav({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const params = useParams<{ id?: string }>();
  const chartId = params.id ?? getActiveChartId();
  const showBezel = chartId != null || pathname.startsWith("/cast");

  return (
    <FaceGateProvider chartId={chartId}>
      {showBezel && (
        <div className="chapter-nav-row">
          <ChapterNav chartId={chartId} />
        </div>
      )}
      {showPathway(pathname, chartId) && (
        <div className="page-inner pt-3 pb-0">
          <ReadingPathway chartId={chartId} />
        </div>
      )}
      {children}
    </FaceGateProvider>
  );
}